import {
  action,
  computed,
  makeObservable,
  observable,
  reaction,
  runInAction
} from 'mobx';

import { apiUrls } from 'shared/entities/domain';
import { AppNotificationType } from 'shared/entities/appNotifications';
import { IRootStore } from 'shared/entities/store/rootStore';
import { ISlaPolicyStore } from 'shared/entities/store/slaPolicyStore';
import {
  ISlaPolicy,
  normalizeSlaPolicy,
  SlaPolicyParamsServer,
  SlaPolicyServer,
  areSlaPolicyPayloadsEqual
} from 'shared/entities/slaPolicy';
import { LoadingStageModel } from 'shared/models/loadingStage';
import { ComponentLoadingStore } from 'stores/componentLoadingStore';

/**
 * Единственный владелец SLA-политики проекта: её читают и настройки чатов, и
 * «Чаты» (доступность сортировки по сроку SLA). Поэтому стор живёт на root
 * уровне и загружается один раз, а потребители не дублируют endpoint и запрос.
 *
 * Стор владеет и жизненным циклом своих запросов: политика привязана к
 * проекту, поэтому смена проекта обрывает летящий GET, а ответ применяется
 * только если он всё ещё актуален для текущего контекста.
 */
export default class SlaPolicyStore
  extends ComponentLoadingStore
  implements ISlaPolicyStore
{
  readonly rootStore: IRootStore;
  readonly loadingStage = new LoadingStageModel();
  readonly savingStage = new LoadingStageModel();

  policy: ISlaPolicy | null = null;

  private abortController: AbortController | null = null;
  private pendingSavePayload: SlaPolicyParamsServer | null = null;
  private policyRevision: number | null = null;
  /** Растёт при сбросе контекста: ответы прошлого проекта уже неприменимы. */
  private contextVersion = 0;

  constructor(rootStore: IRootStore) {
    super();

    this.rootStore = rootStore;

    makeObservable(this, {
      policy: observable.ref,

      sortAvailable: computed,
      isSubmitting: computed,

      applyPolicySnapshot: action,
      save: action
    });
  }

  get canBeLoaded(): boolean {
    return this.rootStore.initialized && this.rootStore.userStore.isAuthorized;
  }

  get shouldBeSkipped(): boolean {
    return !this.rootStore.permissionsStore.canViewProject;
  }

  get loaded(): boolean {
    return this.policy !== null;
  }

  get sortAvailable(): boolean {
    return this.policy?.sortAvailable ?? false;
  }

  get isSubmitting(): boolean {
    return this.savingStage.isLoading;
  }

  applyPolicySnapshot(policy: SlaPolicyServer, revision?: number): boolean {
    if (
      revision !== undefined &&
      this.policyRevision !== null &&
      revision <= this.policyRevision
    ) {
      return false;
    }

    // Политика из WS-события свежее GET, который мог остаться в полёте.
    this.abort();
    this.loadingStage.reset();
    this.policy = normalizeSlaPolicy(policy);

    if (revision !== undefined) {
      this.policyRevision = revision;
    }

    return true;
  }

  initReactions(): void {
    super.initReactions();

    this.addReaction({
      key: 'projectId',
      reaction: reaction(
        () => this.rootStore.projectId,
        () => {
          this.reset();
        }
      )
    });
  }

  async load(): Promise<BaseResponse> {
    if (this.loadingStage.isLoading) {
      return { isError: true };
    }

    const controller = this.startRequest();

    this.loadingStage.loading();

    const response = await this.rootStore.networkStore.api<SlaPolicyServer>(
      apiUrls.PROJECTS_SLA_GET_SETTINGS,
      { config: { signal: controller.signal } }
    );

    // Устаревший ответ не трогает ни политику, ни стадию: иначе отменённый
    // запрос прошлого проекта увёл бы экран в ошибку.
    if (this.abortController !== controller) {
      return { isError: true };
    }

    this.abortController = null;

    runInAction(() => {
      if (response.isError) {
        this.loadingStage.error();
        return;
      }

      this.policy = normalizeSlaPolicy(response.data);
      this.loadingStage.success();
    });

    return { isError: response.isError };
  }

  /**
   * Бэкенд заменяет политику целиком и отвечает актуальным снимком вместе с
   * пересчитанным sort_available, поэтому локально ничего не домысливаем.
   */
  async save(payload: SlaPolicyParamsServer): Promise<BaseResponse> {
    if (this.isSubmitting) {
      this.pendingSavePayload = payload;
      return { isError: false };
    }

    // Снимок GET, начатый до POST, не должен перезаписать сохранённую политику.
    this.abort();
    this.loadingStage.reset();

    const contextVersion = this.contextVersion;

    this.savingStage.loading();

    return this.savePayload(payload, contextVersion);
  }

  private async savePayload(
    payload: SlaPolicyParamsServer,
    contextVersion: number,
    lastSuccessfulPolicy: SlaPolicyServer | null = null
  ): Promise<BaseResponse> {
    const response = await this.rootStore.networkStore.api<SlaPolicyServer>(
      apiUrls.PROJECTS_SLA_SET_SETTINGS,
      { method: 'POST', data: payload }
    );

    // POST нельзя отменить: отмена не отменит запись на сервере. Но ответ,
    // догнавший смену проекта, применять к новому контексту уже нельзя.
    if (contextVersion !== this.contextVersion) {
      return { isError: true };
    }

    const savedPolicy = response.isError ? lastSuccessfulPolicy : response.data;
    const pendingPayload = this.pendingSavePayload;

    this.pendingSavePayload = null;

    if (pendingPayload && !areSlaPolicyPayloadsEqual(pendingPayload, payload)) {
      return this.savePayload(pendingPayload, contextVersion, savedPolicy);
    }

    if (response.isError) {
      runInAction(() => {
        if (savedPolicy) {
          this.policy = normalizeSlaPolicy(savedPolicy);
        }
        this.savingStage.error();
      });
      return { isError: true };
    }

    runInAction(() => {
      this.policy = normalizeSlaPolicy(response.data);
      this.savingStage.success();
    });

    this.rootStore.appNotificationsStore.open({
      title: (t) => t('SlaPolicyStore.notifications.success', { ns: 'stores' }),
      type: AppNotificationType.success
    });

    return { isError: false };
  }

  reset(): void {
    this.abort();
    this.contextVersion += 1;
    this.pendingSavePayload = null;
    this.policy = null;
    this.policyRevision = null;
    this.loadingStage.reset();
    this.savingStage.reset();
  }

  private startRequest(): AbortController {
    this.abort();

    const controller = new AbortController();
    this.abortController = controller;

    return controller;
  }

  private abort(): void {
    this.abortController?.abort();
    this.abortController = null;
  }
}
